<script>
    import { onMount, onDestroy } from 'svelte';
    import { conversations, selectedConversation } from './store.js';
    import { conversationsAPI } from './api.js';
    import { notificationService } from './notificationService.js';
    import { on as rtOn } from './lib/realtime.js';

    let filterStatus = 'all';
    let previousConversations = [];
    let pollingInterval = null;
    let firstLoad = true;
    let rtUnsubs = [];

    function upsertConversation(conv) {
        conversations.update(list => {
            const idx = list.findIndex(c => c.id === conv.id);
            if (idx === -1) return [conv, ...list];
            const next = [...list];
            next[idx] = { ...next[idx], ...conv };
            // Most-recent activity first
            next.sort((a, b) => new Date(b.last_activity_at || 0) - new Date(a.last_activity_at || 0));
            return next;
        });
    }

    const channelIcons = {
        'web': '💬',
        'web_widget': '💬',
        'whatsapp': '📱',
        'facebook': '📘',
        'instagram': '📸',
        'email': '📧',
        'telegram': '✈️',
        'sms': '💬',
        'api': '🔌'
    };

    const channelColors = {
        'web': '#2196f3',
        'web_widget': '#2196f3',
        'whatsapp': '#25d366',
        'facebook': '#0084ff',
        'instagram': '#e4405f',
        'email': '#ff9800',
        'telegram': '#0088cc',
        'sms': '#9c27b0',
        'api': '#607d8b'
    };

    async function loadConversations() {
        try {
            const status = filterStatus === 'all' ? null : filterStatus;
            const convs = await conversationsAPI.getAll(status);

            // Check for new messages (only after first load)
            if (!firstLoad && previousConversations.length > 0) {
                checkForNewMessages(convs);
            }

            previousConversations = JSON.parse(JSON.stringify(convs));
            conversations.set(convs);
            firstLoad = false;
        } catch (error) {
            console.error('Error loading conversations:', error);
        }
    }

    function checkForNewMessages(newConversations) {
        newConversations.forEach(newConv => {
            const oldConv = previousConversations.find(c => c.id === newConv.id);

            if (oldConv) {
                // Check if there are new unread messages
                const hasNewMessages = newConv.unread_count > (oldConv.unread_count || 0);
                const lastActivityChanged = newConv.last_activity_at !== oldConv.last_activity_at;

                if (hasNewMessages && lastActivityChanged) {
                    // Get the latest message text if available
                    const messageText = newConv.subject || 'Nuevo mensaje';
                    const contactName = newConv.contact?.name || 'Contacto desconocido';

                    // Show notification
                    notificationService.notifyNewMessage(
                        contactName,
                        messageText,
                        newConv.id
                    );
                }
            } else if (newConv.unread_count > 0) {
                // Completely new conversation with unread messages
                const contactName = newConv.contact?.name || 'Contacto desconocido';
                const messageText = newConv.subject || 'Nueva conversación';

                notificationService.notifyNewMessage(
                    contactName,
                    messageText,
                    newConv.id
                );
            }
        });
    }

    function startPolling() {
        // Sockets deliver updates in real time; this is only a slow fallback
        pollingInterval = setInterval(() => {
            loadConversations();
        }, 60000);
    }

    function stopPolling() {
        if (pollingInterval) {
            clearInterval(pollingInterval);
            pollingInterval = null;
        }
    }

    // Handle custom event to open conversation from notification click
    function handleOpenConversation(event) {
        const { conversationId } = event.detail;
        const conv = $conversations.find(c => c.id === conversationId);
        if (conv) {
            selectConversation(conv);
        }
    }

    onMount(() => {
        // Request notification permission on first load
        notificationService.requestPermission();

        // Real-time updates via socket; polling stays as a slow fallback
        rtUnsubs = [
            rtOn('new_conversation', (conv) => upsertConversation(conv)),
            rtOn('conversation_updated', (conv) => {
                upsertConversation(conv);
                const prev = previousConversations.find(c => c.id === conv.id);
                if (prev && conv.unread_count > (prev.unread_count || 0)) {
                    notificationService.notifyNewMessage(
                        conv.contact?.name || 'Contacto', conv.subject || 'Nuevo mensaje', conv.id
                    );
                }
                previousConversations = previousConversations.map(c => c.id === conv.id ? conv : c);
            }),
            rtOn('conversation_assigned', (conv) => upsertConversation(conv))
        ];

        startPolling();

        // Listen for notification clicks
        window.addEventListener('openConversation', handleOpenConversation);
    });

    onDestroy(() => {
        stopPolling();
        rtUnsubs.forEach(off => off());
        window.removeEventListener('openConversation', handleOpenConversation);
    });

    function selectConversation(conv) {
        selectedConversation.set(conv);
    }

    function getStatusColor(status) {
        const colors = {
            open: 'var(--success)',
            pending: 'var(--warning)',
            resolved: 'var(--info)',
            closed: 'var(--text-3)'
        };
        return colors[status] || 'var(--text-3)';
    }

    function getChannelIcon(channel) {
        return channelIcons[channel] || channelIcons['web'];
    }

    function getChannelColor(channel) {
        return channelColors[channel] || channelColors['web'];
    }

    function formatTime(dateString) {
        if (!dateString) return '';
        const date = new Date(dateString);
        const now = new Date();
        const diff = now - date;
        const minutes = Math.floor(diff / 60000);
        const hours = Math.floor(diff / 3600000);
        const days = Math.floor(diff / 86400000);

        if (minutes < 1) return 'Just now';
        if (minutes < 60) return `${minutes}m ago`;
        if (hours < 24) return `${hours}h ago`;
        if (days < 7) return `${days}d ago`;
        return date.toLocaleDateString('es-CR', { timeZone: 'America/Costa_Rica' });
    }

    $: if (filterStatus) loadConversations();
</script>

<div class="conversations-panel">
    <div class="panel-header">
        <h2>Bandeja</h2>
        <div class="filter-tabs">
            <button
                class="tab {filterStatus === 'all' ? 'active' : ''}"
                on:click={() => filterStatus = 'all'}
            >
                Todas
            </button>
            <button
                class="tab {filterStatus === 'open' ? 'active' : ''}"
                on:click={() => filterStatus = 'open'}
            >
                Abiertas
            </button>
            <button
                class="tab {filterStatus === 'pending' ? 'active' : ''}"
                on:click={() => filterStatus = 'pending'}
            >
                Pendientes
            </button>
        </div>
    </div>

    <div class="conversations-list">
        {#if $conversations.length === 0}
            <div class="empty-state">
                <p>Sin conversaciones</p>
            </div>
        {:else}
            {#each $conversations as conv (conv.id)}
                <button
                    class="conversation-item {$selectedConversation?.id === conv.id ? 'active' : ''}"
                    on:click={() => selectConversation(conv)}
                >
                    <div class="conversation-avatar">
                        {conv.contact?.name?.charAt(0) || '?'}
                    </div>
                    <div class="conversation-content">
                        <div class="conversation-header">
                            <span class="contact-name">{conv.contact?.name || 'Sin nombre'}</span>
                            <span class="time">{formatTime(conv.last_activity_at)}</span>
                        </div>
                        <div class="conversation-preview">
                            <span class="subject" class:unread-text={conv.unread_count > 0}>
                                {#if conv.last_message}
                                    {#if conv.last_message.attachments?.length}📎 {/if}
                                    {conv.last_message.content || 'Adjunto'}
                                {:else}
                                    {conv.subject || 'Sin mensajes'}
                                {/if}
                            </span>
                            {#if conv.unread_count > 0}
                                <span class="unread-badge">{conv.unread_count}</span>
                            {/if}
                        </div>
                        <div class="conversation-meta">
                            <span
                                class="channel-badge"
                                style="color: {getChannelColor(conv.channel)};"
                                title="{conv.channel}"
                            >
                                <span class="channel-icon">{getChannelIcon(conv.channel)}</span>
                                {conv.channel_obj?.name || conv.channel}
                            </span>
                            <span
                                class="status-badge"
                                style="background-color: {getStatusColor(conv.status)}"
                            >
                                {conv.status}
                            </span>
                            {#if conv.priority && conv.priority !== 'medium'}
                                <span class="priority-badge priority-{conv.priority}">
                                    {conv.priority}
                                </span>
                            {/if}
                        </div>
                    </div>
                </button>
            {/each}
        {/if}
    </div>
</div>

<style>
    .conversations-panel {
        width: 380px;
        background: var(--surface-0);
        border-right: 1px solid var(--border-1);
        display: flex;
        flex-direction: column;
        height: 100%;
        flex-shrink: 0;
    }

    .panel-header {
        padding: 16px 20px;
        border-bottom: 1px solid var(--border-1);
    }

    .panel-header h2 {
        margin: 0 0 12px 0;
        font-size: var(--text-xl);
        color: var(--text-1);
    }

    .filter-tabs {
        display: flex;
        gap: 6px;
    }

    .tab {
        flex: 1;
        padding: 7px 8px;
        background: none;
        border: 1px solid var(--border-1);
        border-radius: var(--radius);
        cursor: pointer;
        font-size: var(--text-sm);
        font-weight: 500;
        color: var(--text-2);
        transition: background var(--transition-fast), color var(--transition-fast), border-color var(--transition-fast);
    }

    .tab:hover {
        background: var(--surface-1);
    }

    .tab.active {
        background: var(--accent);
        border-color: var(--accent);
        color: var(--text-inverse);
    }

    .conversations-list {
        flex: 1;
        overflow-y: auto;
    }

    .empty-state {
        display: flex;
        align-items: center;
        justify-content: center;
        height: 200px;
        color: var(--text-3);
    }

    .conversation-item {
        display: flex;
        gap: 12px;
        padding: 14px 20px;
        background: var(--surface-0);
        border: none;
        border-bottom: 1px solid var(--border-1);
        border-left: 3px solid transparent;
        cursor: pointer;
        transition: background var(--transition-fast);
        width: 100%;
        text-align: left;
    }

    .conversation-item:hover {
        background: var(--surface-1);
    }

    .conversation-item.active {
        background: var(--accent-soft);
        border-left-color: var(--accent);
    }

    .conversation-avatar {
        width: 44px;
        height: 44px;
        border-radius: 50%;
        background: linear-gradient(135deg, var(--accent) 0%, var(--accent-hover) 100%);
        color: var(--text-inverse);
        display: flex;
        align-items: center;
        justify-content: center;
        font-weight: 600;
        font-size: var(--text-lg);
        flex-shrink: 0;
    }

    .conversation-content {
        flex: 1;
        min-width: 0;
    }

    .conversation-header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        margin-bottom: 4px;
    }

    .contact-name {
        font-weight: 600;
        color: var(--text-1);
        font-size: var(--text-base);
    }

    .time {
        font-size: var(--text-xs);
        color: var(--text-3);
    }

    .conversation-preview {
        display: flex;
        justify-content: space-between;
        align-items: center;
        margin-bottom: 6px;
    }

    .subject {
        font-size: var(--text-sm);
        color: var(--text-2);
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
    }
    .subject.unread-text {
        color: var(--text-1);
        font-weight: 600;
    }

    .unread-badge {
        background: var(--accent);
        color: var(--text-inverse);
        padding: 1px 8px;
        border-radius: var(--radius-full);
        font-size: var(--text-xs);
        font-weight: 600;
        flex-shrink: 0;
        margin-left: 8px;
    }

    .conversation-meta {
        display: flex;
        gap: 6px;
        flex-wrap: wrap;
    }

    .channel-badge {
        padding: 2px 8px;
        border-radius: var(--radius-full);
        font-size: 11px;
        font-weight: 600;
        display: inline-flex;
        align-items: center;
        gap: 4px;
        text-transform: capitalize;
        background: var(--surface-2);
    }

    .channel-icon {
        font-size: 13px;
    }

    .status-badge {
        padding: 2px 8px;
        border-radius: var(--radius-full);
        font-size: var(--text-xs);
        color: var(--text-inverse);
        text-transform: capitalize;
        font-weight: 500;
    }

    .priority-badge {
        padding: 2px 8px;
        border-radius: var(--radius-full);
        font-size: var(--text-xs);
        text-transform: capitalize;
        font-weight: 500;
    }

    .priority-high {
        background: var(--warning);
        color: var(--text-inverse);
    }

    .priority-urgent {
        background: var(--danger);
        color: var(--text-inverse);
    }

    .priority-low {
        background: var(--text-3);
        color: var(--text-inverse);
    }

    @media (max-width: 900px) {
        .conversations-panel {
            width: 100%;
        }
    }

    @media (max-width: 768px) {
        /* The topbar already says "Bandeja"; keep just the filter tabs here */
        .panel-header { padding: 10px 12px; }
        .panel-header h2 { display: none; }
        .conversation-item { padding: 12px 14px; gap: 10px; }
        /* Bigger tap targets for the status filter */
        .tab { padding: 9px 8px; }
    }
</style>
